import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { 
  Loader2, Users, UserPlus, Shield, ShieldCheck, Crown, 
  Ban, UserX, Trash2, MoreVertical, Mail, CheckCircle,
  XCircle, AlertTriangle, Edit, Building2, Plus, Sparkles,
  Activity, Cpu, RefreshCw, TrendingUp, TrendingDown
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "@/components/ui/sonner";
import axios from "axios";
import { useSearchParams } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL || "";

const getRoleIcon = (role) => {
  switch (role) {
    case "owner":
      return <Crown className="w-4 h-4 text-purple-600" />;
    case "admin":
      return <ShieldCheck className="w-4 h-4 text-blue-600" />;
    default:
      return <Shield className="w-4 h-4 text-gray-400" />;
  }
};

const getRoleBadge = (role) => {
  switch (role) {
    case "owner":
      return <Badge className="bg-purple-600 hover:bg-purple-700">Owner</Badge>;
    case "admin":
      return <Badge className="bg-blue-600 hover:bg-blue-700">Admin</Badge>;
    default:
      return <Badge variant="secondary">Benutzer</Badge>;
  }
};

const getStatusBadge = (user) => {
  if (user.is_banned) {
    return <Badge variant="destructive">Gesperrt</Badge>;
  }
  if (!user.is_active) {
    return <Badge variant="outline" className="text-orange-600 border-orange-600">Inaktiv</Badge>;
  }
  return <Badge variant="outline" className="text-green-600 border-green-600">Aktiv</Badge>;
};

export const AdminPanel = ({ currentUser }) => {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'organizations' ? 'organizations' : 'users';
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);
  const [error, setError] = useState("");

  // Invite dialog
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteFirstName, setInviteFirstName] = useState("");
  const [inviteLastName, setInviteLastName] = useState("");
  const [inviteRole, setInviteRole] = useState("user");
  const [isInviting, setIsInviting] = useState(false);
  // New org options for invite
  const [createNewOrg, setCreateNewOrg] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");

  // Edit dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editRole, setEditRole] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  // Delete confirmation
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Organization dialog
  const [showOrgDialog, setShowOrgDialog] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [isCreatingOrg, setIsCreatingOrg] = useState(false);

  // LLM Status (only for m.bauer@rangruppe.de)
  const [llmStatus, setLlmStatus] = useState(null);
  const [isLoadingLlm, setIsLoadingLlm] = useState(false);

  const currentRole = currentUser?.role || "user";
  const isOwner = currentRole === "owner";
  const isSuperAdmin = currentUser?.is_super_admin || false;

  // Check if user is m.bauer for LLM status visibility
  const isLlmStatusVisible = currentUser?.email?.toLowerCase() === "m.bauer@rangruppe.de";

  useEffect(() => {
    fetchUsers();
    if (isSuperAdmin) {
      fetchOrganizations();
    }
    if (isLlmStatusVisible) {
      fetchLlmStatus();
    }
  }, [isSuperAdmin, isLlmStatusVisible]);

  const fetchLlmStatus = async () => {
    setIsLoadingLlm(true);
    try {
      const response = await axios.get(`${API}/api/admin/llm-status`);
      setLlmStatus(response.data);
    } catch (err) {
      console.error("Error fetching LLM status:", err);
    } finally {
      setIsLoadingLlm(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/users`);
      setUsers(response.data.users);
    } catch (err) {
      setError(err.response?.data?.detail || "Benutzer konnten nicht geladen werden");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    setIsLoadingOrgs(true);
    try {
      const response = await axios.get(`${API}/api/organizations`);
      setOrganizations(response.data.organizations || []);
    } catch (err) {
      console.error("Error fetching organizations:", err);
    } finally {
      setIsLoadingOrgs(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setIsInviting(true);

    const fullName = `${inviteFirstName.trim()} ${inviteLastName.trim()}`.trim();

    try {
      // Use new endpoint if creating new org
      if (createNewOrg && isSuperAdmin) {
        await axios.post(`${API}/api/admin/users/invite-with-org`, {
          email: inviteEmail,
          name: fullName,
          role: "owner", // New org owner
          create_new_org: true,
          new_org_name: newOrgName,
        });
        toast.success(`${inviteEmail} mit neuer Organisation "${newOrgName}" eingeladen`);
        fetchOrganizations();
      } else {
        await axios.post(`${API}/api/admin/users/invite`, {
          email: inviteEmail,
          name: fullName,
          role: inviteRole,
        });
        toast.success(`Einladung an ${inviteEmail} gesendet`);
      }
      setShowInviteDialog(false);
      setInviteEmail("");
      setInviteFirstName("");
      setInviteLastName("");
      setInviteRole("user");
      setCreateNewOrg(false);
      setNewOrgName("");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Einladung fehlgeschlagen");
    } finally {
      setIsInviting(false);
    }
  };

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    setIsCreatingOrg(true);
    try {
      await axios.post(`${API}/api/organizations`, { name: orgName });
      toast.success(`Organisation "${orgName}" erstellt`);
      setShowOrgDialog(false);
      setOrgName("");
      fetchOrganizations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Organisation konnte nicht erstellt werden");
    } finally {
      setIsCreatingOrg(false);
    }
  };

  const handleSwitchOrg = async (orgId) => {
    try {
      const response = await axios.post(`${API}/api/organizations/${orgId}/switch`);
      toast.success(response.data.message);
      // Reload page to update user context
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Wechsel fehlgeschlagen");
    }
  };

  const handleDeleteOrg = async (org) => {
    if (!window.confirm(`Möchten Sie die Organisation "${org.name}" wirklich löschen?\n\nHinweis: Alle Benutzer dieser Organisation müssen zuerst entfernt werden.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/api/organizations/${org.id}`);
      toast.success(`Organisation "${org.name}" wurde gelöscht`);
      fetchOrganizations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const handleOpenEdit = (user) => {
    setEditUser(user);
    const nameParts = (user.name || "").split(" ");
    setEditFirstName(nameParts[0] || "");
    setEditLastName(nameParts.slice(1).join(" ") || "");
    setEditRole(user.role);
    setShowEditDialog(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setIsUpdating(true);

    const fullName = `${editFirstName.trim()} ${editLastName.trim()}`.trim();

    try {
      await axios.put(`${API}/api/admin/users/${editUser.id}`, {
        name: fullName !== editUser.name ? fullName : undefined,
        role: editRole !== editUser.role ? editRole : undefined,
      });
      toast.success("Benutzer aktualisiert");
      setShowEditDialog(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Aktualisierung fehlgeschlagen");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await axios.put(`${API}/api/admin/users/${user.id}`, {
        is_active: !user.is_active,
      });
      toast.success(user.is_active ? "Benutzer deaktiviert" : "Benutzer aktiviert");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Aktion fehlgeschlagen");
    }
  };

  const handleToggleBan = async (user) => {
    try {
      await axios.put(`${API}/api/admin/users/${user.id}`, {
        is_banned: !user.is_banned,
      });
      toast.success(user.is_banned ? "Sperrung aufgehoben" : "Benutzer gesperrt");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Aktion fehlgeschlagen");
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/api/admin/users/${deleteUser.id}`);
      toast.success("Benutzer gelöscht");
      setShowDeleteDialog(false);
      setDeleteUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Löschen fehlgeschlagen");
    } finally {
      setIsDeleting(false);
    }
  };

  const canEditUser = (user) => {
    // Owner can edit everyone except themselves for role changes
    if (isOwner) return true;
    // Admin can edit users but not owners or other admins' roles
    if (currentRole === "admin" && user.role !== "owner") return true;
    return false;
  };

  const canDeleteUser = (user) => {
    // Cannot delete yourself
    if (user.id === currentUser?.id) return false;
    // Cannot delete owners
    if (user.role === "owner") return false;
    // Admin cannot delete other admins
    if (currentRole === "admin" && user.role === "admin") return false;
    return true;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
            <Users className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Administration</h1>
            <p className="text-muted-foreground">
              {currentUser?.organization_name || 'Keine Organisation'}
              {isSuperAdmin && <Badge variant="secondary" className="ml-2 text-[10px]">Super-Admin</Badge>}
            </p>
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="users" className="gap-2">
            <Users className="w-4 h-4" />
            Benutzer ({users.length})
          </TabsTrigger>
          {isSuperAdmin && (
            <TabsTrigger value="organizations" className="gap-2">
              <Building2 className="w-4 h-4" />
              Organisationen ({organizations.length})
            </TabsTrigger>
          )}
          {isLlmStatusVisible && (
            <TabsTrigger value="llm-status" className="gap-2">
              <Cpu className="w-4 h-4" />
              LLM-Status
            </TabsTrigger>
          )}
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowInviteDialog(true)} data-testid="invite-user-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Benutzer einladen
            </Button>
          </div>
          
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Benutzer</TableHead>
                    <TableHead>Rolle</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Erstellt</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                            {getRoleIcon(user.role)}
                          </div>
                          <div>
                            <div className="font-medium">{user.name}</div>
                            <div className="text-sm text-muted-foreground">{user.email}</div>
                          </div>
                          {user.id === currentUser?.id && (
                            <Badge variant="outline" className="text-xs">Sie</Badge>
                          )}
                          {user.is_super_admin && (
                            <Badge variant="secondary" className="text-[10px]">Super</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{getRoleBadge(user.role)}</TableCell>
                      <TableCell>{getStatusBadge(user)}</TableCell>
                      <TableCell>
                        {user.created_at ? new Date(user.created_at).toLocaleDateString("de-DE") : "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        {canEditUser(user) && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`user-actions-${user.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleOpenEdit(user)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Bearbeiten
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {user.id !== currentUser?.id && (
                            <>
                              <DropdownMenuItem onClick={() => handleToggleActive(user)}>
                                {user.is_active ? (
                                  <>
                                    <XCircle className="w-4 h-4 mr-2" />
                                    Deaktivieren
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    Aktivieren
                                  </>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleToggleBan(user)}>
                                {user.is_banned ? (
                                  <>
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    Entsperren
                                  </>
                                ) : (
                                  <>
                                    <Ban className="w-4 h-4 mr-2" />
                                    Sperren
                                  </>
                                )}
                              </DropdownMenuItem>
                              {canDeleteUser(user) && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    onClick={() => {
                                      setDeleteUser(user);
                                      setShowDeleteDialog(true);
                                    }}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Löschen
                                  </DropdownMenuItem>
                                </>
                              )}
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      </TabsContent>

      {/* Organizations Tab (Super-Admin only) */}
      {isSuperAdmin && (
        <TabsContent value="organizations" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowOrgDialog(true)} data-testid="create-org-btn">
              <Plus className="w-4 h-4 mr-2" />
              Organisation erstellen
            </Button>
          </div>
          
          {isLoadingOrgs ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : organizations.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Building2 className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Keine Organisationen</h3>
                <p className="text-muted-foreground text-center mb-4">
                  Erstellen Sie Ihre erste Organisation.
                </p>
                <Button onClick={() => setShowOrgDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Organisation erstellen
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {organizations.map((org) => (
                <Card key={org.id} className="relative overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-primary" />
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Building2 className="w-5 h-5 text-primary" />
                      {org.name}
                    </CardTitle>
                    <CardDescription>{org.slug}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Benutzer</span>
                      <Badge variant="secondary">{org.user_count}</Badge>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Owner</span>
                      <span className="font-medium truncate max-w-[150px]" title={org.owner_email}>
                        {org.owner_name || org.owner_email || '-'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Status</span>
                      {org.is_active ? (
                        <Badge variant="outline" className="text-green-600 border-green-600">Aktiv</Badge>
                      ) : (
                        <Badge variant="outline" className="text-red-600 border-red-600">Inaktiv</Badge>
                      )}
                    </div>
                    <Separator />
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => handleSwitchOrg(org.id)}
                      >
                        <Sparkles className="w-4 h-4 mr-1" />
                        Wechseln
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => handleDeleteOrg(org)}
                        data-testid={`delete-org-${org.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      )}

      {/* LLM Status Tab (only for m.bauer@rangruppe.de) */}
      {isLlmStatusVisible && (
        <TabsContent value="llm-status" className="space-y-4">
          <div className="flex justify-end">
            <Button 
              variant="outline" 
              onClick={fetchLlmStatus} 
              disabled={isLoadingLlm}
              data-testid="refresh-llm-status-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoadingLlm ? 'animate-spin' : ''}`} />
              Aktualisieren
            </Button>
          </div>

          {isLoadingLlm ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : llmStatus ? (
            <div className="space-y-4">
              {/* Warning Banner */}
              {llmStatus.warning && (
                <Alert variant="destructive">
                  <AlertTriangle className="w-4 h-4" />
                  <AlertDescription>
                    {llmStatus.warning_message || "Hohe Fallback-Rate in den letzten 24h!"}
                  </AlertDescription>
                </Alert>
              )}

              {/* Stats Cards */}
              <div className="grid gap-4 md:grid-cols-3">
                {/* 24h Stats */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Activity className="w-4 h-4 text-primary" />
                      Letzte 24 Stunden
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Anfragen gesamt</span>
                        <span className="font-semibold">{llmStatus.last_24h?.total_requests || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Erfolgreich</span>
                        <span className="font-semibold text-green-600">{llmStatus.last_24h?.successful || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Fehlgeschlagen</span>
                        <span className="font-semibold text-red-600">{llmStatus.last_24h?.failed || 0}</span>
                      </div>
                      <Separator />
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Fallback genutzt</span>
                        <Badge variant={llmStatus.last_24h?.fallback_rate > 30 ? "destructive" : "secondary"}>
                          {llmStatus.last_24h?.fallback_used || 0} ({llmStatus.last_24h?.fallback_rate || 0}%)
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* 7d Stats */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-primary" />
                      Letzte 7 Tage
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Anfragen gesamt</span>
                        <span className="font-semibold">{llmStatus.last_7d?.total_requests || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Erfolgreich</span>
                        <span className="font-semibold text-green-600">{llmStatus.last_7d?.successful || 0}</span>
                      </div>
                      <Separator />
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Fallback-Rate</span>
                        <Badge variant={llmStatus.last_7d?.fallback_rate > 30 ? "destructive" : "secondary"}>
                          {llmStatus.last_7d?.fallback_rate || 0}%
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Provider Status */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Cpu className="w-4 h-4 text-primary" />
                      Provider-Status
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {Object.entries(llmStatus.providers || {}).map(([provider, status]) => (
                        <div key={provider} className="flex justify-between items-center">
                          <span className="text-muted-foreground capitalize">
                            {provider.replace('_', ' ')}
                          </span>
                          {status.configured ? (
                            <Badge variant="outline" className="text-green-600 border-green-600">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Aktiv
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-red-600 border-red-600">
                              <XCircle className="w-3 h-3 mr-1" />
                              Fehlt
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Errors */}
              {llmStatus.recent_errors && llmStatus.recent_errors.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-destructive" />
                      Letzte Fehler (24h)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {llmStatus.recent_errors.map((error, idx) => (
                        <div key={idx} className="text-sm p-2 bg-muted rounded">
                          <div className="flex justify-between text-xs text-muted-foreground mb-1">
                            <span>{error.provider} / {error.model}</span>
                            <span>{error.created_at ? new Date(error.created_at).toLocaleTimeString('de-DE') : '-'}</span>
                          </div>
                          <p className="text-xs text-destructive truncate">{error.error}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Usage by Provider */}
              {llmStatus.last_24h?.by_provider && Object.keys(llmStatus.last_24h.by_provider).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-medium">Nutzung nach Provider (24h)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Provider</TableHead>
                          <TableHead className="text-right">Gesamt</TableHead>
                          <TableHead className="text-right">Erfolg</TableHead>
                          <TableHead className="text-right">Fehler</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Object.entries(llmStatus.last_24h.by_provider).map(([provider, stats]) => (
                          <TableRow key={provider}>
                            <TableCell className="capitalize">{provider.replace('_', ' ')}</TableCell>
                            <TableCell className="text-right">{stats.total}</TableCell>
                            <TableCell className="text-right text-green-600">{stats.success}</TableCell>
                            <TableCell className="text-right text-red-600">{stats.failed}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Cpu className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Keine Daten</h3>
                <p className="text-muted-foreground text-center">
                  LLM-Status konnte nicht geladen werden.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      )}
      </Tabs>

      {/* Invite User Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Neuen Benutzer einladen</DialogTitle>
            <DialogDescription>
              Senden Sie eine Einladung per Email. Der Benutzer kann dann sein Passwort festlegen.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInvite} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="inviteFirstName">Vorname</Label>
                <Input
                  id="inviteFirstName"
                  value={inviteFirstName}
                  onChange={(e) => setInviteFirstName(e.target.value)}
                  placeholder="Max"
                  required
                  data-testid="invite-firstname-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="inviteLastName">Nachname</Label>
                <Input
                  id="inviteLastName"
                  value={inviteLastName}
                  onChange={(e) => setInviteLastName(e.target.value)}
                  placeholder="Mustermann"
                  required
                  data-testid="invite-lastname-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="inviteEmail">Email</Label>
              <Input
                id="inviteEmail"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="max@beispiel.de"
                required
                data-testid="invite-email-input"
              />
            </div>
            
            {/* Super-Admin: Option to create new org */}
            {isSuperAdmin && (
              <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="createNewOrg" className="font-medium">Neue Organisation erstellen</Label>
                    <p className="text-xs text-muted-foreground">
                      Der Benutzer wird Owner seiner eigenen Organisation
                    </p>
                  </div>
                  <Switch
                    id="createNewOrg"
                    checked={createNewOrg}
                    onCheckedChange={setCreateNewOrg}
                    data-testid="create-new-org-switch"
                  />
                </div>
                
                {createNewOrg && (
                  <div className="space-y-2">
                    <Label htmlFor="newOrgName">Organisations-Name</Label>
                    <Input
                      id="newOrgName"
                      value={newOrgName}
                      onChange={(e) => setNewOrgName(e.target.value)}
                      placeholder="z.B. Firma GmbH"
                      required={createNewOrg}
                      data-testid="new-org-name-input"
                    />
                  </div>
                )}
              </div>
            )}
            
            {/* Role selection (hidden if creating new org) */}
            {!createNewOrg && (
              <div className="space-y-2">
                <Label htmlFor="inviteRole">Rolle</Label>
                <Select value={inviteRole} onValueChange={setInviteRole}>
                  <SelectTrigger data-testid="invite-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Benutzer</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    {isOwner && <SelectItem value="owner">Owner</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowInviteDialog(false)}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={isInviting} data-testid="send-invite-btn">
                {isInviting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Senden...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Einladung senden
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Benutzer bearbeiten</DialogTitle>
            <DialogDescription>
              {editUser?.email}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="editFirstName">Vorname</Label>
                <Input
                  id="editFirstName"
                  value={editFirstName}
                  onChange={(e) => setEditFirstName(e.target.value)}
                  required
                  data-testid="edit-firstname-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editLastName">Nachname</Label>
                <Input
                  id="editLastName"
                  value={editLastName}
                  onChange={(e) => setEditLastName(e.target.value)}
                  data-testid="edit-lastname-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="editRole">Rolle</Label>
              <Select 
                value={editRole} 
                onValueChange={setEditRole}
                disabled={editUser?.id === currentUser?.id && editUser?.role === "owner"}
              >
                <SelectTrigger data-testid="edit-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Benutzer</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  {isOwner && <SelectItem value="owner">Owner</SelectItem>}
                </SelectContent>
              </Select>
              {editUser?.id === currentUser?.id && editUser?.role === "owner" && (
                <p className="text-xs text-muted-foreground">
                  Als Owner können Sie Ihre eigene Rolle nicht ändern.
                </p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={isUpdating} data-testid="save-user-btn">
                {isUpdating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Speichern...
                  </>
                ) : (
                  "Speichern"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Benutzer löschen
            </DialogTitle>
            <DialogDescription>
              Sind Sie sicher, dass Sie <strong>{deleteUser?.name}</strong> ({deleteUser?.email}) löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              disabled={isDeleting}
              data-testid="confirm-delete-btn"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Löschen...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Endgültig löschen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
